import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import Add from '@mui/icons-material/Add';
import ContentCopy from '@mui/icons-material/ContentCopy';
import Refresh from '@mui/icons-material/Refresh';
import Block from '@mui/icons-material/Block';
import Autorenew from '@mui/icons-material/Autorenew';
import { toast } from 'react-toastify';
import {
  ApiKeyView,
  createApiKey,
  listApiKeys,
  regenerateApiKey,
  suspendApiKey,
} from '../../../services/request/ApiKeys';

const ApiKeysSettings: React.FC = () => {
  const [rows, setRows] = useState<ApiKeyView[]>([]);
  const [loading, setLoading] = useState(false);
  const [dlg, setDlg] = useState(false);
  const [name, setName] = useState('Voice assistant');
  const [domains, setDomains] = useState('http://localhost:5173');
  const [plainKey, setPlainKey] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await listApiKeys();
      setRows(res.data ?? []);
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Failed to load API keys');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const create = async () => {
    try {
      const allowedDomains = domains
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      if (allowedDomains.length === 0) {
        toast.error('Add at least one allowed origin');
        return;
      }
      const res = await createApiKey({ name: name.trim(), allowedDomains });
      if (res.data?.plainTextKey) {
        setPlainKey(res.data.plainTextKey);
        toast.success(res.data.message || 'API key created');
      }
      setDlg(false);
      await load();
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Failed to create API key');
    }
  };

  const copyKey = async () => {
    if (!plainKey) return;
    try {
      await navigator.clipboard.writeText(plainKey);
      toast.success('API key copied');
    } catch {
      toast.error('Copy failed');
    }
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Box>
            <Typography variant="h6" fontWeight={600}>
              API keys (voice assistant)
            </Typography>
            <Typography variant="body2" color="text.secondary">
              GET-only access. Currently scoped to Dashboard API. Each key requires allowed browser origins (Origin header).
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Button startIcon={<Refresh />} onClick={() => void load()} disabled={loading}>
              Refresh
            </Button>
            <Button variant="contained" startIcon={<Add />} onClick={() => setDlg(true)}>
              Create key
            </Button>
          </Stack>
        </Stack>

        {plainKey && (
          <Alert
            severity="warning"
            sx={{ mb: 2 }}
            action={
              <IconButton color="inherit" size="small" onClick={() => void copyKey()}>
                <ContentCopy />
              </IconButton>
            }
          >
            Copy this key now — it will not be shown again:
            <Box component="span" sx={{ display: 'block', fontFamily: 'monospace', mt: 1 }}>
              {plainKey}
            </Box>
          </Alert>
        )}

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Prefix</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Allowed origins</TableCell>
                <TableCell>Scopes</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell>{r.name}</TableCell>
                  <TableCell>
                    <Typography variant="caption" fontFamily="monospace">
                      {r.keyPrefix}…
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={r.status}
                      color={r.status === 'ACTIVE' ? 'success' : 'default'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">{r.allowedDomains?.join(', ')}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">{r.allowedScopes?.join(', ')}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Button
                        size="small"
                        startIcon={<Autorenew />}
                        onClick={async () => {
                          const res = await regenerateApiKey(r.id);
                          if (res.data?.plainTextKey) {
                            setPlainKey(res.data.plainTextKey);
                            toast.success('Regenerated');
                          }
                          await load();
                        }}
                      >
                        Regenerate
                      </Button>
                      {r.status === 'ACTIVE' && (
                        <Button
                          size="small"
                          color="warning"
                          startIcon={<Block />}
                          onClick={async () => {
                            await suspendApiKey(r.id);
                            toast.info('Suspended');
                            await load();
                          }}
                        >
                          Suspend
                        </Button>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Typography variant="body2" color="text.secondary">
                      No API keys yet.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Alert severity="info" sx={{ mt: 2 }}>
          Voice app usage: send header <code>X-API-Key: wnz_…</code> and ensure the browser sends a matching{' '}
          <code>Origin</code> (e.g. your voice UI host). Example:{' '}
          <code>GET /api/v1/all/dashboard</code>
        </Alert>
      </CardContent>

      <Dialog open={dlg} onClose={() => setDlg(false)} fullWidth maxWidth="sm">
        <DialogTitle>Create API key</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Name" value={name} onChange={(e) => setName(e.target.value)} fullWidth />
            <TextField
              label="Allowed origins (comma-separated)"
              value={domains}
              onChange={(e) => setDomains(e.target.value)}
              fullWidth
              helperText="e.g. http://localhost:5173, https://voice.yourdomain.com"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDlg(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => void create()}>
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default ApiKeysSettings;
